import * as React from "react";
import { useDispatch } from "react-redux";
import { SpinButton, Button, Divider } from "@fluentui/react-components";
import CommonTooltip from "../../../common/CommonTooltip";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { updateIconMargin, updateImage, updateShapeMargin, updateTextPadding } from "../../../../state/cellStylesSlice";
import { useSelector } from "react-redux";
import { onSelection } from "../../../../helpers/selection";
import "./submenuitem.css";
import { XIcon } from "@phosphor-icons/react";
import { Add16Filled } from "@fluentui/react-icons";

function getValue(currentStyle, objectName, propertyName, currentStyleSheet) {
  const value =
    objectName === "text" && currentStyle
      ? (currentStyle[propertyName] ?? (propertyName === "padding" ? currentStyleSheet.cellPadding : ""))
      : objectName === "shape" && currentStyle?.shape
        ? (currentStyle?.shape[propertyName] ?? "")
        : objectName === "icon" && currentStyle?.iconStyle
          ? (currentStyle?.iconStyle[propertyName] ?? "")
          : currentStyle?.img
            ? (currentStyle?.img[propertyName] ?? "")
            : "";
  return value;
}

function withPlusSign(value) {
  return value > 0 ? `+${value}` : value;
}

const Spinner = ({
  propertyName,
  objectName,
  currentStyleSheet,
  currentStyle,
  dispatch,
  updateAction,
  structure,
  selection,
}) => {
  const value = getValue(currentStyle, objectName, propertyName, currentStyleSheet);
  // console.log("Margin spinner value", {
  //   value,
  //   propertyName,
  //   objectName,
  //   currentStyleSheet,
  //   currentStyle,
  //   defaultValue,
  // });

  return (
    <div>
      <SpinButton
        size="small"
        style={{ minWidth: 0, width: 48 }}
        value={isNaN(value) ? "" : value}
        displayValue={value === "" ? "" : objectName === "shape" ? withPlusSign(value * -1) : value}
        min={-100}
        max={100}
        step={objectName === "shape" ? -1 : 1}
        onChange={(_, d) => {
          const newValue = isNaN(d.value) ? parseInt(d.displayValue ?? "0") : d.value;
          onSelection(dispatch, updateAction, { structure, selection }, { [propertyName]: newValue });
        }}
      />
    </div>
  );
};

Spinner.propTypes = {
  propertyName: PropTypes.string,
  currentStyleSheet: PropTypes.object,
  currentStyle: PropTypes.object,
  dispatch: PropTypes.any,
  updateAction: PropTypes.any,
  structure: PropTypes.any,
  selection: PropTypes.any,
  objectName: PropTypes.string, // text || shape || img
};

const PaddingSettingRow = ({
  propertyName,
  currentStyleSheet,
  currentStyle,
  dispatch,
  updateAction,
  structure,
  selection,
  objectName,
  defaultPixel,
  defaultPercentage,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <CommonTooltip content={t(`menu.style.text.${propertyName}`)} relationship="label">
        <div style={{ alignSelf: "center" }}>
          {objectName === "shape" && propertyName === "margin" ? t("menu.shape.size.all") : t(propertyName)}
        </div>
      </CommonTooltip>
      <Spinner
        propertyName={propertyName}
        currentStyleSheet={currentStyleSheet}
        currentStyle={currentStyle}
        dispatch={dispatch}
        updateAction={updateAction}
        selection={selection}
        structure={structure}
        objectName={objectName}
        defaultValue={defaultPixel}
      />
      <Add16Filled style={{ alignSelf: "center", justifySelf: "center" }} />
      <Spinner
        propertyName={propertyName + "Perc"}
        currentStyleSheet={currentStyleSheet}
        currentStyle={currentStyle}
        dispatch={dispatch}
        updateAction={updateAction}
        selection={selection}
        structure={structure}
        objectName={objectName}
        defaultValue={defaultPercentage}
      />
      <CommonTooltip content={t(`menu.style.text.${propertyName}.reset`)}>
        <Button
          size="small"
          appearance="subtle"
          icon={<XIcon />}
          onClick={() =>
            onSelection(
              dispatch,
              updateAction,
              { structure, selection },
              { [propertyName]: null, [propertyName + "Perc"]: null }
            )
          }
        />
      </CommonTooltip>
    </>
  );
};

PaddingSettingRow.propTypes = {
  propertyName: PropTypes.string,
  currentStyleSheet: PropTypes.object,
  currentStyle: PropTypes.object,
  dispatch: PropTypes.any,
  updateAction: PropTypes.any,
  structure: PropTypes.any,
  selection: PropTypes.any,
  objectName: PropTypes.string, // text || shape || img || icon
  defaultPixel: PropTypes.any,
  defaultPercentage: PropTypes.any,
};

const PaddingSubMenuItem = ({ currentStyleSheet, currentStyle, propertyName, objectName }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const structure = useSelector((state) => state.gridd.present.structure);
  const selection = useSelector((state) => state.selection);
  const updateAction =
    objectName === "text"
      ? updateTextPadding
      : objectName === "shape"
        ? updateShapeMargin
        : objectName === "icon"
          ? updateIconMargin
          : updateImage;

  return (
    <div className="submenuitem-container">
      <div className="margin-rows-container">
        <div />
        <div>px</div>
        <div />
        <div>%</div>
        <div />

        <PaddingSettingRow
          propertyName={propertyName}
          currentStyleSheet={currentStyleSheet}
          currentStyle={currentStyle}
          dispatch={dispatch}
          updateAction={updateAction}
          selection={selection}
          structure={structure}
          objectName={objectName}
        />

        <div className="row-divider" />

        <PaddingSettingRow
          propertyName={propertyName + "Left"}
          currentStyleSheet={currentStyleSheet}
          currentStyle={currentStyle}
          dispatch={dispatch}
          updateAction={updateAction}
          selection={selection}
          structure={structure}
          objectName={objectName}
          defaultPixel={getValue(currentStyle, objectName, propertyName, currentStyleSheet)}
          defaultPercentage={getValue(currentStyle, objectName, propertyName, currentStyleSheet, true)}
        />

        <PaddingSettingRow
          propertyName={propertyName + "Top"}
          currentStyleSheet={currentStyleSheet}
          currentStyle={currentStyle}
          dispatch={dispatch}
          updateAction={updateAction}
          selection={selection}
          structure={structure}
          objectName={objectName}
          defaultPixel={getValue(currentStyle, objectName, propertyName, currentStyleSheet)}
          defaultPercentage={getValue(currentStyle, objectName, propertyName, currentStyleSheet, true)}
        />

        <PaddingSettingRow
          propertyName={propertyName + "Right"}
          currentStyleSheet={currentStyleSheet}
          currentStyle={currentStyle}
          dispatch={dispatch}
          updateAction={updateAction}
          selection={selection}
          structure={structure}
          objectName={objectName}
          defaultPixel={getValue(currentStyle, objectName, propertyName, currentStyleSheet)}
          defaultPercentage={getValue(currentStyle, objectName, propertyName, currentStyleSheet, true)}
        />

        <PaddingSettingRow
          propertyName={propertyName + "Bottom"}
          currentStyleSheet={currentStyleSheet}
          currentStyle={currentStyle}
          dispatch={dispatch}
          updateAction={updateAction}
          selection={selection}
          structure={structure}
          objectName={objectName}
          defaultPixel={getValue(currentStyle, objectName, propertyName, currentStyleSheet)}
          defaultPercentage={getValue(currentStyle, objectName, propertyName, currentStyleSheet, true)}
        />

        <Divider className="row-divider" />

        <CommonTooltip content={t("zIndex.tooltip")}>
          <div>{t("zIndex")}</div>
        </CommonTooltip>
        <SpinButton
          size="small"
          style={{ minWidth: 0, width: 48 }}
          value={
            objectName === "text"
              ? currentStyle?.zIndex
              : objectName === "shape"
                ? currentStyle?.shape?.zIndex
                : objectName === "icon"
                  ? currentStyle?.iconStyle?.zIndex
                  : currentStyle?.img?.zIndex
          }
          min={-1000}
          max={1000}
          step={1}
          onChange={(_, d) =>
            onSelection(
              dispatch,
              updateAction,
              { structure, selection },
              { zIndex: isNaN(d.value) ? parseInt(d.displayValue) : d.value }
            )
          }
        />
        <CommonTooltip content={t(`menu.style.${objectName}.zIndex.reset`)}>
          <Button
            size="small"
            appearance="subtle"
            icon={<XIcon />}
            onClick={() => onSelection(dispatch, updateAction, { structure, selection }, { zIndex: null })}
          />
        </CommonTooltip>
      </div>
    </div>
  );
};

PaddingSubMenuItem.propTypes = {
  propertyName: PropTypes.string, // padding || margin
  objectName: PropTypes.string, // text || shape || img || icon
  currentStyle: PropTypes.object,
  currentStyleSheet: PropTypes.object,
};

export default PaddingSubMenuItem;
